import React, { useEffect, useState } from 'react'

import {
  type BreakdownSelection,
  getBreakdownLabel,
  isRatingBreakdown,
} from 'features/charts-v2/components/breakdown-selection-utils'
import {
  Container,
  Footer,
  Group,
  GroupHeader,
  ParentRowCountLabel,
  PaginationSummary,
  PaginationWrapper,
  Swatch,
  ValueRow,
  ValueText,
} from 'features/charts-v2/components/chart-legend.styles'
import { LegendPaginationNav } from 'features/charts-v2/components/LegendPaginationNav/LegendPaginationNav'
import { TooManyDatapointsState } from 'features/charts-v2/components/TooManyDatapointsState'
import { useBreakdownColorMap } from 'features/charts-v2/context/breakdown-color-map-context'
import type { Chart } from 'features/charts-v2/types'
import type { SortDirection } from 'features/charts-v2/types/chart-strategy'
import {
  buildBreakdownLegendEntries,
  getRatingStarLabel,
} from 'features/charts-v2/utils/breakdown-color-map'
import { countParentRows } from 'features/charts-v2/utils/parent-row-count'

const LEGEND_PAGE_SIZE = 10

interface ChartLegendProps {
  chartData: Chart | null
  selectedBreakdowns: BreakdownSelection[]
  onDataPointHover?: (key: string | null) => void
  onSegmentHover?: (segmentKey: string | null) => void
  hidePrimaryGroup?: boolean
  sortDirection?: SortDirection
  /** When true, shows the "too many datapoints" state instead of painting. */
  isOverDatapointLimit?: boolean
}

const ChartLegend: React.FC<ChartLegendProps> = ({
  chartData,
  selectedBreakdowns,
  onDataPointHover,
  onSegmentHover,
  hidePrimaryGroup = false,
  sortDirection = 'DESC',
  isOverDatapointLimit = false,
}) => {
  const [page, setPage] = useState(1)
  // Stable color map from Container — keeps sidebar swatches in sync with the
  // chart lines and LegendDataTable checkboxes after filter changes.
  const contextColorMap = useBreakdownColorMap()

  // Build groups unconditionally (empty when no data) so hook order is stable
  // above the early return below.
  const groups =
    !chartData || selectedBreakdowns.length === 0
      ? []
      : selectedBreakdowns
          .map((breakdown, i) => {
            const label = getBreakdownLabel(breakdown)
            const isPrimary = i === 0
            const entries = buildBreakdownLegendEntries(
              breakdown,
              chartData,
              i,
              sortDirection,
            )
            // Primary breakdown: override swatch colors with the stable map so
            // they match the chart lines (it only covers the primary dimension).
            const coloredEntries =
              isPrimary && contextColorMap.size > 0
                ? entries.map((e) => ({
                    ...e,
                    color: contextColorMap.get(e.value) ?? e.color,
                  }))
                : entries
            // Rating displays as "5 Stars"; `value` stays raw "5" for hover-sync
            // with the chart (keyed on the backend category) and the React key.
            const isRating = isRatingBreakdown(breakdown)
            const labeledEntries = coloredEntries.map((e) => ({
              ...e,
              displayValue: isRating ? getRatingStarLabel(e.value) : e.value,
            }))
            return { label, entries: labeledEntries, isPrimary }
          })
          .filter(({ isPrimary }) => !(hidePrimaryGroup && isPrimary))

  // Flatten all groups into a single ordered list for pagination.
  const flatEntries = groups.flatMap((g) =>
    g.entries.map((e) => ({
      ...e,
      groupLabel: g.label,
      isPrimary: g.isPrimary,
    })),
  )
  const total = flatEntries.length
  const parentRowCount = countParentRows(chartData)

  // Clamp page when the list shrinks (e.g. breakdown changes).
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(total / LEGEND_PAGE_SIZE))
    if (page > maxPage) setPage(maxPage)
  }, [total, page])

  if (isOverDatapointLimit) return <TooManyDatapointsState textWidth="100%" />

  if (total === 0) return <Container />

  const pageEntries = flatEntries.slice(
    (page - 1) * LEGEND_PAGE_SIZE,
    page * LEGEND_PAGE_SIZE,
  )

  const start = (page - 1) * LEGEND_PAGE_SIZE + 1
  const end = Math.min(page * LEGEND_PAGE_SIZE, total)
  const totalPages = Math.ceil(total / LEGEND_PAGE_SIZE)
  const canPrev = page > 1
  const canNext = page < totalPages

  // Re-group the current page so group headers still appear for visible rows.
  type PageGroup = {
    label: string
    isPrimary: boolean
    entries: typeof pageEntries
  }
  const pageGroups: PageGroup[] = []
  pageEntries.forEach((entry) => {
    const last = pageGroups[pageGroups.length - 1]
    if (last && last.label === entry.groupLabel) {
      last.entries.push(entry)
    } else {
      pageGroups.push({
        label: entry.groupLabel,
        isPrimary: entry.isPrimary,
        entries: [entry],
      })
    }
  })

  return (
    <Container>
      {pageGroups.map(({ label, entries, isPrimary }) => (
        <Group key={label}>
          <GroupHeader>{label}</GroupHeader>
          {entries.map(({ value, color, displayValue }) => (
            <ValueRow
              key={`${label}-${value}`}
              $interactive={isPrimary || !!onSegmentHover}
              onMouseEnter={
                isPrimary
                  ? () => onDataPointHover?.(value)
                  : onSegmentHover
                    ? () => onSegmentHover(value)
                    : undefined
              }
              onMouseLeave={
                isPrimary
                  ? () => onDataPointHover?.(null)
                  : onSegmentHover
                    ? () => onSegmentHover(null)
                    : undefined
              }
            >
              <Swatch style={{ background: color }} />
              <ValueText title={displayValue}>{displayValue}</ValueText>
            </ValueRow>
          ))}
        </Group>
      ))}
      <Footer>
        <ParentRowCountLabel>
          {`${parentRowCount.toLocaleString()} ${parentRowCount === 1 ? 'datapoint' : 'datapoints'}`}
        </ParentRowCountLabel>
        {total > LEGEND_PAGE_SIZE && (
          <PaginationWrapper>
            <PaginationSummary>{`${start}-${end} of ${total}`}</PaginationSummary>
            <LegendPaginationNav
              canPrev={canPrev}
              canNext={canNext}
              onFirst={() => canPrev && setPage(1)}
              onPrev={() => canPrev && setPage(page - 1)}
              onNext={() => canNext && setPage(page + 1)}
              onLast={() => canNext && setPage(totalPages)}
            />
          </PaginationWrapper>
        )}
      </Footer>
    </Container>
  )
}

export { ChartLegend }
